/**
 * Soft paywall state — calculator-run counter persisted in localStorage.
 *
 * Cohort 1 rule (chosen by product): the FIRST free calculation runs without
 * any gate. The SECOND calculation surfaces an email-gate. Once the visitor
 * unlocks (by handing over an email), every subsequent calculator-on-the-site
 * runs ungated.
 *
 * Storage keys:
 *   bl_calc_run_count   — integer counter, increments on every calc result.
 *   bl_calc_unlocked    — "1" once the visitor has unlocked via /lead-capture.
 *
 * Authenticated users are exempt from the gate entirely (they already
 * gave us an email at signup).
 */
const COUNT_KEY = "bl_calc_run_count";
const UNLOCK_KEY = "bl_calc_unlocked";

export function incrementCalcRun() {
    try {
        const n = Number(localStorage.getItem(COUNT_KEY) || "0") || 0;
        const next = n + 1;
        localStorage.setItem(COUNT_KEY, String(next));
        return next;
    } catch {
        return 0;
    }
}

export function getCalcRunCount() {
    try {
        return Number(localStorage.getItem(COUNT_KEY) || "0") || 0;
    } catch {
        return 0;
    }
}

export function isCalcUnlocked() {
    try {
        return localStorage.getItem(UNLOCK_KEY) === "1";
    } catch {
        return false;
    }
}

export function markCalcUnlocked() {
    try {
        localStorage.setItem(UNLOCK_KEY, "1");
    } catch {
        /* non-critical */
    }
}

/**
 * Returns true when the calculator should render the soft-paywall overlay
 * INSTEAD OF the result. Called right before setResult() in the calculator
 * submit handler.
 *
 * Default rule: the first free calculation (count=0) runs ungated; the
 * second (count>=1) is gated until the visitor unlocks.
 *
 * @param {object} opts
 * @param {boolean} opts.isAuthenticated  exempt signed-in users
 * @param {number}  opts.gateAt           free-runs allowed before gating (default 1)
 */
export function shouldGateCalcRun({ isAuthenticated, gateAt = 1 } = {}) {
    if (isAuthenticated) return false;
    if (isCalcUnlocked()) return false;
    return getCalcRunCount() >= gateAt;
}
